<script setup lang="ts">
   import { computed, ref } from 'vue';
   import LmsLogoutButton from '~/components/LmsLogoutButton.vue';
   import { storeToRefs } from 'pinia';
   import useAuthStore from '~/stores/auth';
   import { useUserProfileMenu } from '~/composables/useUserProfileMenu';
   import { useQueryClient } from '@tanstack/vue-query';

   const authStore = useAuthStore();
   const { user, selectedPortal, availablePortals } = storeToRefs(authStore);
   const { showProfileFeatures, profileNavigationItems, adminMenuItem } = useUserProfileMenu();
   const config = useRuntimeConfig();
   const queryClient = useQueryClient();

   const currentPortal = computed(() => {
      return selectedPortal.value || config.public.portalBrand as string;
   });

   const showPortalSwitcher = computed(() => {
      return !config.public.singleBrand && availablePortals.value.length > 1;
   });

   const handlePortalSwitch = async (portalSlug: string) => {
      if (portalSlug === currentPortal.value) {
         return;
      }

      menu.value.hide();
      await authStore.switchPortal(portalSlug);
      queryClient.invalidateQueries();
   };

   const items = computed(() => {
      const menuItems: any[] = [
         {
            separator: true,
         },
         {
            label: 'Profile',
            show: showProfileFeatures, // This show controls the visibility of the "Profile" submenu label
            items: profileNavigationItems.value,
         },
      ];
      if (adminMenuItem.value) {
         menuItems.push({ separator: true });
         menuItems.push(adminMenuItem.value);
      }
      return menuItems;
   });

   const menu = ref();

   //functions
   const toggle = (event: Event) => {
      menu.value.toggle(event);
   };
</script>

<template>
   <div class="card flex justify-center" data-testid="user-menu">
      <div v-if="true">
         <Button
            type="button"
            icon="pi pi-user"
            @click="toggle"
            aria-haspopup="true"
            aria-controls="overlay_menu" />
         <Menu
            :model="items"
            class="mt-0 w-full pt-0 md:max-w-72"
            :popup="true"
            ref="menu">
            <template #start>
               <button
                  v-ripple
                  class="relative flex w-full cursor-pointer items-start overflow-hidden rounded-none border-0 bg-transparent p-2 pl-4 transition-colors duration-200 hover:bg-surface-100 dark:hover:bg-surface-800">
                  <span class="inline-flex w-full flex-col items-start">
                     <span class="w-full truncate font-bold">
                        {{ user?.user?.name }}
                     </span>
                     <span class="w-full truncate" :title="user?.user?.email">
                        {{ user?.user?.email }}
                     </span>
                  </span>
               </button>
               <div v-if="showPortalSwitcher" class="border-t border-gray-200 px-4 py-2">
                  <span class="mb-1 block text-xs font-semibold text-gray-500">Switch Portal</span>
                  <select
                     :value="currentPortal"
                     class="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                     @change="handlePortalSwitch(($event.target as HTMLSelectElement).value)">
                     <option
                        v-for="portal in availablePortals"
                        :key="portal.portal"
                        :value="portal.portal">
                        {{ portal.brand_name }}
                     </option>
                  </select>
               </div>
            </template>
            <!-- <template #submenulabel="{ item }">
               <span class="font-bold text-brand-title" v-if="item.show">
                  {{ item.label }}
               </span>
               <span v-else=""></span>
            </template> -->
            <template #item="{ item, props }">
               <router-link
                  class="mt-0"
                  v-if="item.to"
                  v-slot="{ href, navigate }"
                  :to="item.to"
                  custom>
                  <a
                     v-ripple
                     :href="href"
                     v-bind="props.action"
                     @click="navigate">
                     <span :class="item.icon" />
                     <span class="ml-2">{{ item.label }}</span>
                  </a>
               </router-link>
               <a
                  v-else-if="item.url"
                  v-ripple
                  :href="item.url"
                  :target="item.target || '_self'"
                  :rel="item.target === '_blank' ? 'noopener noreferrer' : undefined"
                  v-bind="props.action">
                  <span :class="item.icon" />
                  <span class="ml-2">{{ item.label }}</span>
               </a>
            </template>
            <template #end>
               <LmsLogoutButton location="userMenu" />
            </template>
         </Menu>
      </div>
   </div>
</template>
